import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Zap, CheckCircle, Calendar, ClipboardCheck, ChevronDown, Pencil } from '../lib/icons';
import { useImageSlot } from '../lib/image-slots-client';
import { useEditMode } from '../contexts/EditModeContext';
import ContentSlot from './ContentSlot';

const FALLBACK_VIDEO = '/website intro video nintendo .mp4';

const Hero = () => {
  const [showCheckInDropdown, setShowCheckInDropdown] = useState(false);
  const [showScheduleDropdown, setShowScheduleDropdown] = useState(false);
  const { slot: videoSlot } = useImageSlot('hero-bg-video');
  const editMode = useEditMode();
  const videoSrc = videoSlot?.image_url ?? FALLBACK_VIDEO;

  const openSchedule = () => setShowScheduleDropdown(d => !d);

  const scrollToServices = () => {
    const element = document.getElementById('repair-selector');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-[#0D0D0D] via-gray-900 to-[#0D0D0D] text-[#E0E0E0] py-20 overflow-hidden">
      {/* Background Video */}
      <div className="absolute inset-0 z-0">
        <video
          key={videoSrc}
          autoPlay
          muted
          loop
          playsInline
          src={videoSrc}
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D0D0D]/80 via-gray-900/70 to-[#0D0D0D]/80" />
        {editMode.active && (
          <button
            type="button"
            onClick={() => editMode.onEditSlot('hero-bg-video', videoSlot ?? null, FALLBACK_VIDEO)}
            className="absolute top-2 right-2 z-30 flex items-center gap-1.5 bg-[#39FF14] text-[#0D0D0D] text-xs font-bold px-2.5 py-1.5 rounded-lg shadow-[0_0_10px_rgba(57,255,20,0.5)]"
          >
            <Pencil className="h-3.5 w-3.5" /> Edit Hero Video
          </button>
        )}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-5xl sm:text-6xl font-bold mb-6 leading-tight">
            <ContentSlot slotId="hero-heading-1" fallback="Same Day" contentType="heading" pageSection="hero" label="Headline Part 1" />{' '}
            <span className="text-[#00BFFF]">
              <ContentSlot slotId="hero-heading-2" fallback="Console Repairs" contentType="heading" pageSection="hero" label="Headline Part 2 (blue)" />
            </span>
          </h2>
          <ContentSlot
            slotId="hero-subtitle"
            fallback="Professional PS5, Xbox & Nintendo Switch repairs with lightning-fast 3-4 hour turnaround"
            contentType="paragraph"
            as="p"
            pageSection="hero"
            label="Hero Subtitle"
            className="text-xl sm:text-2xl mb-8 text-[#B0B0B0] max-w-3xl mx-auto"
          />

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <button
              onClick={scrollToServices}
              className="bg-[#FF1744] hover:bg-[#39FF14] text-white hover:text-[#0D0D0D] px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg w-full sm:w-auto"
            >
              <ContentSlot slotId="hero-cta-fix" fallback="What Are You Fixing?" contentType="button" pageSection="hero" label="CTA: Fix Button" />
            </button>
            <div className="relative">
              <button
                onClick={openSchedule}
                className="inline-flex items-center justify-center gap-2 border-2 border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-[#0D0D0D] px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-200 w-full sm:w-auto"
              >
                <Calendar className="h-5 w-5" />
                <ContentSlot slotId="hero-cta-schedule" fallback="Schedule" contentType="button" pageSection="hero" label="CTA: Schedule Button" />
                <ChevronDown className="h-4 w-4" />
              </button>
              {showScheduleDropdown && (
                <div className="absolute top-full mt-2 left-0 right-0 sm:left-auto sm:right-auto sm:min-w-[200px] bg-gray-900 border-2 border-[#39FF14] rounded-lg shadow-xl z-50 overflow-hidden">
                  <Link
                    to="/book-repair"
                    className="block px-6 py-3 text-[#E0E0E0] hover:bg-[#39FF14] hover:text-[#0D0D0D] transition-colors duration-200 font-medium"
                    onClick={() => setShowScheduleDropdown(false)}
                  >
                    San Diego
                  </Link>
                  <Link
                    to="/book-repair"
                    className="block px-6 py-3 text-[#E0E0E0] hover:bg-[#39FF14] hover:text-[#0D0D0D] transition-colors duration-200 font-medium border-t border-gray-700"
                    onClick={() => setShowScheduleDropdown(false)}
                  >
                    Temecula
                  </Link>
                </div>
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => setShowCheckInDropdown(!showCheckInDropdown)}
                className="inline-flex items-center justify-center gap-2 border-2 border-[#FF1744] text-[#FF1744] hover:bg-[#FF1744] hover:text-[#0D0D0D] px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-200 w-full sm:w-auto"
              >
                <ClipboardCheck className="h-5 w-5" />
                <ContentSlot slotId="hero-cta-checkin" fallback="Check In" contentType="button" pageSection="hero" label="CTA: Check In Button" />
                <ChevronDown className="h-4 w-4" />
              </button>
              {showCheckInDropdown && (
                <div className="absolute top-full mt-2 left-0 right-0 sm:left-auto sm:right-auto sm:min-w-[200px] bg-gray-900 border-2 border-[#FF1744] rounded-lg shadow-xl z-50 overflow-hidden">
                  <a
                    href="https://gamefixsolutions.repairshopr.com/check_ins"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-6 py-3 text-[#E0E0E0] hover:bg-[#FF1744] hover:text-[#0D0D0D] transition-colors duration-200 font-medium"
                    onClick={() => setShowCheckInDropdown(false)}
                  >
                    San Diego
                  </a>
                  <a
                    href="https://mobileiphonerepairsd.repairshopr.com/check_ins"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-6 py-3 text-[#E0E0E0] hover:bg-[#FF1744] hover:text-[#0D0D0D] transition-colors duration-200 font-medium border-t border-gray-700"
                    onClick={() => setShowCheckInDropdown(false)}
                  >
                    Temecula
                  </a>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto mb-12">
            <div className="flex items-center justify-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <Clock className="h-8 w-8 text-[#39FF14]" />
              <div className="text-left">
                <h3 className="text-lg font-semibold">
                  <ContentSlot slotId="hero-stat-fast-title" fallback="3-4 Hour Service" contentType="heading" pageSection="hero" label="Stat: Fast Turnaround Title" />
                </h3>
                <p className="text-[#B0B0B0]">
                  <ContentSlot slotId="hero-stat-fast-sub" fallback="Xbox & PlayStation repairs" contentType="label" pageSection="hero" label="Stat: Fast Turnaround Subtitle" />
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <Zap className="h-8 w-8 text-[#FF1744]" />
              <div className="text-left">
                <h3 className="text-lg font-semibold">
                  <ContentSlot slotId="hero-stat-hdmi-title" fallback="HDMI Specialists" contentType="heading" pageSection="hero" label="Stat: HDMI Title" />
                </h3>
                <p className="text-[#B0B0B0]">
                  <ContentSlot slotId="hero-stat-hdmi-sub" fallback="Expert port replacement" contentType="label" pageSection="hero" label="Stat: HDMI Subtitle" />
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <CheckCircle className="h-8 w-8 text-[#00BFFF]" />
              <div className="text-left">
                <h3 className="text-lg font-semibold">
                  <ContentSlot slotId="hero-stat-exp-title" fallback="15 Years Experience" contentType="heading" pageSection="hero" label="Stat: Experience Title" />
                </h3>
                <p className="text-[#B0B0B0]">
                  <ContentSlot slotId="hero-stat-exp-sub" fallback="Collective expertise" contentType="label" pageSection="hero" label="Stat: Experience Subtitle" />
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;
